import React from 'react';
import { ChevronDown, ChevronUp, Volume2 } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { cn } from '../lib/utils';

function CollapsibleSection({ title, icon: Icon, isOpen, onToggle, children, badge }) {
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-4 bg-[#0f133a]/80 hover:bg-[#1a1f50]/80 transition-colors" data-testid="btn-toggle-sonido-directo">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-[#aa2aff] to-[#7012b2]">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <span className="font-semibold text-white text-lg">{title}</span>
          {badge && <span className="px-2 py-0.5 bg-[#aa2aff] text-white text-xs rounded-full">{badge}</span>}
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>
      {isOpen && <div className="p-4 bg-[#080c2d]/50">{children}</div>}
    </div>
  );
}

export function SonidoDirectoSection({ isOpen, onToggle, data = {}, onChange, formatPrice, currency, settings = {} }) {
  const priceBasicPro = settings.price_sonido_basicopro || 350;
  const pricePro = settings.price_sonido_pro || 997;

  const basicProTotal = priceBasicPro * (data.basicProHoras || 0);
  const proTotal = pricePro * (data.proHoras || 0);
  const subtotal = basicProTotal + proTotal;

  const handleChange = (field, value) => onChange({ ...data, [field]: value });
  const badge = subtotal > 0 ? formatPrice(subtotal) : null;

  return (
    <CollapsibleSection title="Sonido Directo" icon={Volume2} isOpen={isOpen} onToggle={onToggle} badge={badge}>
      <div className="space-y-3">
        {/* BásicoPro */}
        <div className={cn("p-4 rounded-xl border transition-all", data.basicProHoras > 0 ? "border-[#aa2aff] bg-[#aa2aff]/10" : "border-white/10 bg-white/5")}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <h4 className="text-white font-semibold text-sm">Paquete BásicoPro</h4>
              <p className="text-slate-400 text-xs mt-1">2 Lavaliers RodeXPro</p>
              <p className="text-slate-400 text-xs mt-0.5">{formatPrice(priceBasicPro)} {currency} / hora</p>
            </div>
            <div className="text-center">
              <Label className="text-slate-500 text-[10px]">Horas</Label>
              <Input type="number" min="0" max="99" value={data.basicProHoras || ''} onChange={(e) => handleChange('basicProHoras', parseInt(e.target.value) || 0)}
                className="bg-[#080c2d]/50 border-[#272b96] text-white w-20 h-8 text-sm focus:border-[#aa2aff]" data-testid="sonido-basicopro-horas" />
            </div>
          </div>
          {basicProTotal > 0 && <p className="text-[#aa2aff] text-xs font-semibold mt-2">= {formatPrice(basicProTotal)} {currency}</p>}
        </div>

        {/* Pro */}
        <div className={cn("p-4 rounded-xl border transition-all", data.proHoras > 0 ? "border-[#aa2aff] bg-[#aa2aff]/10" : "border-white/10 bg-white/5")}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <h4 className="text-white font-semibold text-sm">Paquete Pro</h4>
              <p className="text-slate-400 text-xs mt-1">1 Boom + 2 Lavaliers / Sound Devices / ScratchTrack</p>
              <p className="text-slate-400 text-xs mt-0.5">{formatPrice(pricePro)} {currency} / hora</p>
            </div>
            <div className="text-center">
              <Label className="text-slate-500 text-[10px]">Horas</Label>
              <Input type="number" min="0" max="99" value={data.proHoras || ''} onChange={(e) => handleChange('proHoras', parseInt(e.target.value) || 0)}
                className="bg-[#080c2d]/50 border-[#272b96] text-white w-20 h-8 text-sm focus:border-[#aa2aff]" data-testid="sonido-pro-horas" />
            </div>
          </div>
          {proTotal > 0 && <p className="text-[#aa2aff] text-xs font-semibold mt-2">= {formatPrice(proTotal)} {currency}</p>}
        </div>

        {subtotal > 0 && (
          <div className="pt-3 border-t border-white/10 flex justify-between items-center">
            <span className="text-white font-semibold">Subtotal Sonido Directo:</span>
            <span className="text-[#aa2aff] font-bold text-lg">{formatPrice(subtotal)} {currency}</span>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

export function calcSonidoDirectoTotal(data = {}, settings = {}) {
  return (settings.price_sonido_basicopro || 350) * (data.basicProHoras || 0) + (settings.price_sonido_pro || 997) * (data.proHoras || 0);
}
